import React, { useState, useMemo } from 'react';
import { clone } from 'ramda';
import { Clickable } from '@moda/om';
import ChevronUpIcon from '@moda/icons/chevron-up-12';
import ChevronDownIcon from '@moda/icons/chevron-down-12';
import ExitIcon from '@moda/icons/exit-16';
import { capitalizeWord } from '../../lib/strings/capitalizeWord';
import { ProductDetailsFragment } from '../../generated/types/ProductDetailsFragment';
import { useCountry } from '../CountrySelector';
import './SizeGuide.scss';
import { Country } from '../../constants';

export type SizeGuideProps = {
  category: ProductDetailsFragment['category'];
  sizeEquivalents: ProductDetailsFragment['sizeEquivalents'];
  onClose?(): void;
};

type SizeGuideTableProps = {
  sizeEquivalents: ProductDetailsFragment['sizeEquivalents'];
};

type SizeEquivalents = SizeGuideProps['sizeEquivalents'];

type GuideSizes = {
  [nameOrRegion: string]: string;
};

export const DEFAULT_GUIDE_SIZE = 'US';
const GUIDE_SIZES: GuideSizes = {
  Australia: 'AU',
  China: 'CN',
  Italy: 'IT',
  France: 'FR',
  'United Kingdom': 'UK',
  'United States of America': 'US',
  europe: 'EU'
};
const SIZE_GUIDE_BASED_MESSAGE = `This product is based on ${DEFAULT_GUIDE_SIZE} size.`;
const SIZE_GUIDE_CHART_MESSAGE =
  'Size and cut may vary between brands; use this chart as a general guide.';

const getCategory = (category: SizeGuideProps['category']): string => {
  if (!category || !category[0]) {
    return '';
  }

  const parts = category[0].split('_');

  return parts.length > 1
    ? `${capitalizeWord(parts[0])} ${capitalizeWord(parts[1])}`
    : capitalizeWord(parts[0]);
};

export const getCountrySizeCode = (country: Country | undefined): string => {
  if (!country) {
    return DEFAULT_GUIDE_SIZE;
  }

  return GUIDE_SIZES[country.name] || GUIDE_SIZES[country.region] || DEFAULT_GUIDE_SIZE;
};

const getScaleName = (scaleEquivalent: string | null) => {
  return scaleEquivalent?.split(' ')[0] || '';
};

const getSizeBasedMessage = (sizeEquivalents: SizeEquivalents): string => {
  if (sizeEquivalents.length === 0) {
    return SIZE_GUIDE_BASED_MESSAGE;
  }

  const scaleName = getScaleName(sizeEquivalents[0][0]);

  return scaleName
    ? SIZE_GUIDE_BASED_MESSAGE.replace(DEFAULT_GUIDE_SIZE, scaleName)
    : SIZE_GUIDE_BASED_MESSAGE;
};

const sortSizeColumns = (
  sizeEquivalents: SizeEquivalents,
  countrySizeCode: string
): SizeEquivalents => {
  const countryColumnIndex = sizeEquivalents[0].findIndex(
    size => getScaleName(size) === countrySizeCode
  );

  if (countryColumnIndex === -1) {
    return sizeEquivalents;
  }

  sizeEquivalents.forEach(row => {
    const countrySize = row[countryColumnIndex];

    row[countryColumnIndex] = row[1];
    row[1] = countrySize;
  });

  return sizeEquivalents;
};

const getNormalizedSizeEquivalents = (
  sizeEquivalents: SizeEquivalents,
  countrySizeCode: string
): SizeEquivalents => {
  return sizeEquivalents.length
    ? sortSizeColumns(clone(sizeEquivalents), countrySizeCode)
    : sizeEquivalents;
};

const getScaleSizes = (sizeEquivalents: SizeEquivalents, scaleName: string) => {
  if (sizeEquivalents.length === 0) {
    return [];
  }

  const index = sizeEquivalents[0].findIndex(
    scaleEquivalent => getScaleName(scaleEquivalent) === scaleName
  );

  return index !== -1 ? sizeEquivalents.map(sizeScale => [sizeScale[0], sizeScale[index]]) : [];
};

const getScaleNames = (sizeEquivalents: SizeEquivalents) => {
  if (sizeEquivalents.length === 0) {
    return [];
  }

  return sizeEquivalents[0].map(scaleEquivalent => getScaleName(scaleEquivalent));
};

const SizeGuideTable: React.FC<SizeGuideTableProps> = ({ sizeEquivalents }) => {
  return (
    <table className="SizeGuide__table">
      <thead>
        <tr>
          {getScaleNames(sizeEquivalents).map((scaleName, i) => (
            <th className="SizeGuide__th" key={i}>
              {i === 0 ? 'Standard' : scaleName}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sizeEquivalents.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td className="SizeGuide__td" key={j}>
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SizeGuideTableSmall: React.FC<SizeGuideTableProps> = ({ sizeEquivalents }) => {
  const scaleNames = getScaleNames(sizeEquivalents).slice(1);
  const [scaleName, setScaleName] = useState(scaleNames[0]);
  const [selectorOpen, setSelectorOpen] = useState(false);

  return (
    <table className="SizeGuide__table--small">
      <thead>
        <tr>
          <th className="SizeGuide__th--small">Standard</th>
          <th className="SizeGuide__th--small">
            <Clickable
              className="SizeGuide__clickable--small"
              data-testid="size-guide-selector"
              onClick={() => setSelectorOpen(!selectorOpen)}
            >
              <span className="SizeGuide__scaleName--small">{scaleName}</span>
              {selectorOpen ? (
                <ChevronUpIcon height="0.75em" width="0.75em" />
              ) : (
                <ChevronDownIcon height="0.75em" width="0.75em" />
              )}
            </Clickable>
            {selectorOpen && (
              <span className="SizeGuide__dropdown--small">
                <ul className="SizeGuide__dropdownList--small">
                  {scaleNames.map((scaleName, i) => (
                    <li className="SizeGuide__dropdownListItem--small" key={i}>
                      <Clickable
                        className="SizeGuide__dropdownListItemClickable--small"
                        data-testid={`size-guide-option-${scaleName}`}
                        onClick={() => {
                          setScaleName(scaleName);
                          setSelectorOpen(false);
                        }}
                      >
                        {scaleName}
                      </Clickable>
                    </li>
                  ))}
                </ul>
              </span>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {getScaleSizes(sizeEquivalents, scaleName).map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td className="SizeGuide__td--small" key={j} data-testid="size-guide-small-size">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export const SizeGuide: React.FC<SizeGuideProps> = ({ category, sizeEquivalents, onClose }) => {
  const { country } = useCountry();
  const countrySizeCode = getCountrySizeCode(country);
  const normalizedSizeEquivalents = useMemo(
    () => getNormalizedSizeEquivalents(sizeEquivalents, countrySizeCode),
    [sizeEquivalents, countrySizeCode]
  );

  return (
    <div className="SizeGuide" data-testid="size-guide">
      <Clickable className="SizeGuide__close" data-testid="size-guide-close" onClick={onClose}>
        <ExitIcon />
      </Clickable>

      <div className="SizeGuide__header">Size Guide - {getCategory(category)}</div>
      <div className="SizeGuide__chartMessage">{SIZE_GUIDE_CHART_MESSAGE}</div>
      <div className="SizeGuide__basedMessage">
        {getSizeBasedMessage(normalizedSizeEquivalents)}
      </div>
      <SizeGuideTable sizeEquivalents={normalizedSizeEquivalents} />
      <SizeGuideTableSmall sizeEquivalents={normalizedSizeEquivalents} />
    </div>
  );
};
